/**
 * Croatian — `pageTour` namespace: per-page guided tours ("Vodič kroz ovu stranicu",
 * the ? button in the header). Keys: pageTour.<tourId>.<step>.{tag,title,body}
 * (see composables/pageTours.ts).
 */
export default {
    pageTour: {
        button: {
            label: 'Vodič kroz ovu stranicu',
        },
        controls: {
            next: 'Dalje',
            back: 'Natrag',
            done: 'Gotovo',
            stepOf: 'Korak {current} od {total}',
        },

        dashboard: {
            aggregate: {
                tag: 'Pregled',
                title: 'Izloženost grupe',
                body: 'Zbirna neto pozicija svih zaključanih (LOCKED) ugovora po 15-minutnim intervalima, složena po državi (HR + SI). Iznad nule = KRATKA (premalo nabavljeno), ispod nule = DUGA (višak). Iz istih snimki pozicije CRM nadzorna ploča crta liniju „budućnosnice — gornja granica”.',
            },
            toolbar: {
                tag: 'Filtri grafikona',
                title: 'Raspon, država i podjela',
                body: 'Odaberite OD/DO (zadano: danas + 6 dana), suzite na HR ili SI i uključite podjelu prognoza/futures. Os grafikona prikazuje samo dane koji imaju podatke, pa može biti uža od odabranog raspona — žuta bilješka vas na to upozorava.',
            },
            refresh: {
                tag: 'Osvježavanje',
                title: 'Povucite svježe snimke',
                body: 'Spajanje pozicija pokreće se u pozadini pri svakom prijelazu APPROVED ↔ LOCKED. Nakon što zaključate ugovor, kliknite Osvježi da grafikon pročita novo zapisane snimke.',
            },
            list: {
                tag: 'Popis ugovora',
                title: 'Terminski ugovori',
                body: 'Svaki redak je jedan futures ugovor: strana (BUY/SELL), vrsta proizvoda, država, status, druga strana, razdoblje isporuke, količina i cijena. Klik na redak otvara sažetak desno; strelice gore/dolje i Enter rade s tipkovnice.',
            },
            filters: {
                tag: 'Filtri popisa',
                title: 'Strana, status, država',
                body: 'Popis je zadano filtriran na status APPROVED — nacrti (DRAFT) i zaključani (LOCKED) ugovori ne vide se dok status ne prebacite na „Sve” ili neki drugi. Filtri se pamte dok ste u aplikaciji.',
            },
            newContract: {
                tag: 'Glavna radnja',
                title: 'Novi ugovor',
                body: 'Otvara obrazac za novi ugovor u desnom oknu. Vidljivo samo operaterima s pravom futures:write.',
            },
            viewPosition: {
                tag: 'Pozicija',
                title: 'Prikaži poziciju',
                body: 'Otvara stranicu pozicije tog ugovora: koliko on doprinosi ukupnoj poziciji po danu, u odnosu na prognozu i ostale futures ugovore.',
            },
            form: {
                tag: 'Desno okno',
                title: 'Obrazac novog ugovora',
                body: 'Kad nijedan ugovor nije odabran, ovdje se unosi novi: druga strana, država, strana, proizvod, razdoblje isporuke, MTU popuna u MW i cijena. „Nastavi” stvara ugovor u statusu DRAFT i vodi u uređivač košara.',
            },
            summary: {
                tag: 'Desno okno',
                title: 'Sažetak ugovora',
                body: 'Pregled odabranog ugovora s gumbima za promjenu statusa (Validiraj → Odobri → Zaključaj). „Uredi ugovor” otvara zaglavlje, „Novi ugovor” vraća obrazac.',
            },
        },

        contractEditor: {
            header: {
                tag: 'Zaglavlje',
                title: 'Ugovor i status',
                body: 'Druga strana, strana (BUY/SELL), vrsta proizvoda, izvor profila, status i verzija; linija ispod daje razdoblje isporuke, ukupnu količinu i cijenu. „Uredi ugovor” mijenja zaglavlje (ne košare) — izmjena odobrenog ugovora automatski ga vraća u VALIDATED.',
            },
            status: {
                tag: 'Tijek statusa',
                title: 'Validiraj, odobri, zaključaj',
                body: 'DRAFT → VALIDATED → APPROVED → LOCKED; otkazivanje je konačno. Odobri, Zaključaj i Otključaj traže pravo futures:approve. Tek zaključani (LOCKED) ugovor ulazi u spajanje pozicija — a time u izloženost grupe i u gornju granicu na CRM-u.',
            },
            dayNav: {
                tag: 'Dani isporuke',
                title: 'Navigacija po danima',
                body: 'Košare se uređuju dan po dan. Strelice ili Alt+← / Alt+→ mijenjaju dan; redak ispod kaže koji je dan po redu i koliko intervala ima — 96 normalno, 92 na dan prelaska na ljetno vrijeme, 100 na dan povratka.',
            },
            grid: {
                tag: 'Košare',
                title: 'MW po 15-minutnom intervalu',
                body: 'Četiri reda su četvrtine dana (00–06, 06–12, 12–18, 18–24 po lokalnom satu); vršno razdoblje 08–20 je označeno. Svaka ćelija je snaga u MW za taj interval (MTU), ne energija u MWh. Unos se pamti odmah, ali se sprema tek s „Potvrdi i spremi”.',
            },
            toolbar: {
                tag: 'Alati dana',
                title: 'Kopiraj i poništi dan',
                body: 'Kopiraj prenosi profil ovog dana na odabrane druge dane; Poništi postavlja sve košare dana na nulu. Oboje je onemogućeno na zaključanom ugovoru.',
            },
            liveChart: {
                tag: 'Graf',
                title: 'Profil dana uživo',
                body: 'Crta trenutni dan dok tipkate — brza provjera je li PEAK popunjen samo 08–20, a BASE ravno kroz cijeli dan.',
            },
            footer: {
                tag: 'Spremanje',
                title: 'Ukupno i Potvrdi i spremi',
                body: 'Lijevo: zbroj svih košara, broj dana i koliko ih je nespremljeno. „Potvrdi i spremi” (ili Ctrl+S) otvara pregled po danima i šalje novu verziju; „Odbaci” vraća na nadzornu ploču. Zaključani ugovor je samo za čitanje.',
            },
            audit: {
                tag: 'Revizija',
                title: 'Povijest promjena',
                body: 'Svako stvaranje, izmjena i promjena statusa s vremenom, izvršiteljem i razlikama prije/poslije. Kartica se otvara i izravno poveznicom koja završava na #audit.',
            },
        },

        contractEditHeader: {
            form: {
                tag: 'Zaglavlje ugovora',
                title: 'Uređivanje zaglavlja',
                body: 'Ovdje se mijenjaju podaci ugovora, ne košare — za profil po intervalima vratite se u uređivač. Spremanje stvara novu verziju; ako je netko u međuvremenu spremio svoju, poslužitelj odbija vašu.',
            },
            counterparty: {
                tag: 'Identifikacija',
                title: 'Druga strana i reference',
                body: 'Naziv druge strane je obavezan; EIC i vanjski ID posla su opcionalni i služe za povezivanje s dokumentima i sustavima druge strane.',
            },
            country: {
                tag: 'Država',
                title: 'HR ili SI',
                body: 'Država određuje tržišni kalendar (vršni sati, praznici) i knjigu pozicija u koju ugovor ulazi. Nakon stvaranja mijenja je samo operater s pravom futures:approve — promjena ponovno razrješava profil.',
            },
            sideProduct: {
                tag: 'Strana i proizvod',
                title: 'BUY/SELL i vrsta proizvoda',
                body: 'BUY smanjuje neto poziciju (nabava), SELL je povećava. BASE puni sve intervale, PEAK samo 08–20, OFF_PEAK samo izvan toga, a CUSTOM ostavlja košare za ručni unos u uređivaču.',
            },
            delivery: {
                tag: 'Razdoblje isporuke',
                title: 'OD / DO i brzi odabir',
                body: 'Čipovi (dan, tjedan, mjesec, kvartal, godina) postavljaju raspon oko datuma početka. Kod MANUAL_BILATERAL ugovora raspon je zaključan jer su košare vezane za taj prozor — za drugi raspon stvorite novi ugovor.',
            },
            profileSource: {
                tag: 'Izvor profila',
                title: 'EPEX_DEFAULT ili MANUAL_BILATERAL',
                body: 'EPEX_DEFAULT sam izračuna košare iz vrste proizvoda i MTU popune; MANUAL_BILATERAL očekuje ručno postavljene košare u uređivaču.',
            },
            volume: {
                tag: 'Količina i cijena',
                title: 'MW, ne MWh',
                body: 'MTU popuna je snaga u MW koja ide u svaki aktivni 15-minutni interval; ukupna količina računa se kao popuna × broj aktivnih intervala. Cijena je u €/MW, valuta uvijek EUR.',
            },
            submit: {
                tag: 'Glavna radnja',
                title: 'Spremi promjene',
                body: 'Šalje izmjenu zaglavlja i vraća u uređivač košara; Odustani vraća bez spremanja. Ako se razdoblje preklapa s postojećim ugovorom, poslužitelj odbija spremanje i iznad prikaže poveznice na sporne ugovore.',
            },
        },

        position: {
            header: {
                tag: 'Zaglavlje',
                title: 'Pozicija jednog ugovora',
                body: 'Stranica je samo za čitanje: pokazuje koliko ovaj ugovor doprinosi ukupnoj poziciji po danu. Redak ispod kaže kad je zadnje spajanje pokrenuto te koliko dana i intervala pokriva.',
            },
            noSnapshot: {
                tag: 'Nema snimke',
                title: 'Spajanje još nije pokrenuto',
                body: 'Snimke pozicije nastaju tek kad se ugovor zaključa (LOCKED) i spajanje prođe. Dok ih nema, nema ni grafa.',
            },
            dayNav: {
                tag: 'Odabir dana',
                title: 'Dan po dan',
                body: 'Padajući izbornik nudi svaki dan razdoblja isporuke; strelice ili Alt+← / Alt+→ prelaze na susjedni dan.',
            },
            toggles: {
                tag: 'Slojevi',
                title: 'Što prikazati',
                body: 'Uključite ili isključite osnovnu prognozu, ovaj ugovor i ostale futures ugovore. Oznaka DUGA/KRATKA je smjer po većini intervala tog dana.',
            },
            chart: {
                tag: 'Graf',
                title: 'Složena pozicija',
                body: 'Slojevi: prognoza, doprinos ovog ugovora i ostatak ostalih futures ugovora; linija je konačna pozicija. SELL ugovor dodaje, BUY oduzima. Ako prognoza još nije obrađena, sloj prognoze je nula.',
            },
            summary: {
                tag: 'Sažetak dana',
                title: 'Brojke za odabrani dan',
                body: 'Zbroj prognoze, doprinosa ovog ugovora i konačne pozicije za dan, uz smjer. Vrijednosti su zbrojevi po intervalima.',
            },
            reference: {
                tag: 'Referenca',
                title: 'Oblici proizvoda',
                body: 'Statične ilustracije kako izgledaju BASE, PEAK i OFF_PEAK profili i kako se zbrajaju u kombiniranu futures poziciju. Nisu podaci ovog ugovora — pravi zbroj po svim ugovorima je na nadzornoj ploči.',
            },
        },
    },
}
